import { createContext, useContext, useMemo, useRef, useState } from "react";

const ComponentContext = createContext();

export const ComponentContextProvider = ({ children }) => {

  // ########## MOTIVATION DATA ##########
  const Motivations = {
    FLAECHE: "Fläche",
    ENERGIEKOSTEN: "Energiekosten",
    AUSBRINGUNG: "Ausbringung",
    INVESTVOLUMEN: "Investvolumen",
  }

  const SliderTexts = {
    "Fläche": {
      heading: "Fläche optimieren",
      text: "Optimale Ausnutzung Ihrer verfügbaren Fläche"
    },
    "Energiekosten": {
      heading: "Energiekosten / Bauteil reduzieren",
      text: "Minimale Anschlussleistung / minimaler Energieverbrauch"
    },
    "Ausbringung": {
      heading: "Jährliche Ausbringungsmenge steigern",
      text: "Maximale Ausbringung"
    },
    "Investvolumen": {
      heading: "Investvolumen optimieren",
      text: "Möglichst günstige Lösung"
    }
  }

  const maxPoints = 13;

  // ########## COMPONENT DATA ##########
  const Materials = useMemo(() => {
    return {
      'Edelstahl': {
        heatCapacity: 0.5,
        heatGradients: {
          'black': {
            'Matt': 1.53,
            'Reflektierend': 1.4535,
          },
          'color': {
            'Matt': 1.071,
            'Reflektierend': 1.01745,
          },
          'white': {
            'Matt': 0.47,
            'Reflektierend': 0.376,
          }
        }
      },
      'Stahl': {
        heatCapacity: 0.45,
        heatGradients: {
          'black': {
            'Matt': 5,
            'Reflektierend': 4.5,
          },
          'color': {
            'Matt': 3.5,
            'Reflektierend': 2.8,
          },
          'white': {
            'Matt': 2,
            'Reflektierend': 1.5,
          }
        }
      },
      'Kohlefaser': {
        heatCapacity: 0.71,
        heatGradients: {
          'black': {
            'Matt': 5,
            'Reflektierend': 4.5,
          },
          'color': {
            'Matt': 3.5,
            'Reflektierend': 2.8,
          },
          'white': {
            'Matt': 2,
            'Reflektierend': 1.5,
          }
        }
      },
      'Kunststoff': {
        heatCapacity: 2,
        heatGradients: {
          'black': {
            'Matt': 5,
            'Reflektierend': 4.5,
          },
          'color': {
            'Matt': 3.5,
            'Reflektierend': 2.8,
          },
          'white': {
            'Matt': 2,
            'Reflektierend': 1.5,
          }
        }
      },
      'Silikon': {
        heatCapacity: 1.4,
        heatGradients: {
          'black': {
            'Matt': 5,
            'Reflektierend': 4.5,
          },
          'color': {
            'Matt': 3.5,
            'Reflektierend': 2.8,
          },
          'white': {
            'Matt': 2,
            'Reflektierend': 1.5,
          }
        }
      },
      'POM': {
        heatCapacity: 1.5,
        heatGradients: {
          'black': {
            'Matt': 5,
            'Reflektierend': 4.5,
          },
          'color': {
            'Matt': 3.5,
            'Reflektierend': 2.8,
          },
          'white': {
            'Matt': 2,
            'Reflektierend': 1.5,
          }
        }
      },
      'Aluminium': {
        heatCapacity: 0.9,
        heatGradients: {
          'black': {
            'Matt': 2.13,
            'Reflektierend': 2.0235,
          },
          'color': {
            'Matt': 1.491,
            'Reflektierend': 1.41645,
          },
          'white': {
            'Matt': 0.26,
            'Reflektierend': 0.208,
          }
        }
      }
    }
  }, [])

  const Colors = [
    'Schwarz',
    'Grau',
    'Rot',
    'Orange',
    'Grün',
    'Blau',
    'Gelb',
    'Weiss'
  ]

  const Surfaces = [
    'Matt',
    'Reflektierend'
  ]

  const Geometry = [
    'Flach',
    'Mit Hinterschnitt'
  ]

  const EnergySources = [
    "Infrarot",
    "Konvektion - Elektrisch",
    "Konvektion - Gas"
  ]

  const distanceFactor = 1.3

  const lateralConstructionWidth = 800
  const spaceAroundVDU = 0

  const irModuleLength = 600

  const securityFactorVDU = 1.3
  const securityFactorHE = 1.2

  const loadingFactor = .6

  const steelWeight = 12;
  const isolationStrength = 90

  const preheatTimeVDU = 5;

  const powerHeatRegisterMini = 3.6;
  const powerHeatRegisterSmall = 10.8
  const powerHeatRegisterBig = 21.6

  // Heat Event(HE) Constants
  const heatEvents = useMemo(() => [
    {
      id: 0,
      innerLength: 600, outerLength: 950,
      innerWidth: 600, outerWidth: 1000,
      innerHeight: 600, outerHeight: 1595,
      power: 7.2, price: 14360
    },
    {
      id: 1,
      innerLength: 600, outerLength: 950,
      innerWidth: 600, outerWidth: 1000,
      innerHeight: 900, outerHeight: 1890,
      power: 14.4, price: 16530
    },
    {
      id: 2,
      innerLength: 750, outerLength: 1190,
      innerWidth: 750, outerWidth: 1670,
      innerHeight: 1250, outerHeight: 2150,
      power: 18, price: 19470
    },
    {
      id: 3,
      innerLength: 1000, outerLength: 1440,
      innerWidth: 1000, outerWidth: 1960,
      innerHeight: 1500, outerHeight: 2305,
      power: 26.4, price: 24130
    },
    {
      id: 4,
      innerLength: 1250, outerLength: 1680,
      innerWidth: 1250, outerWidth: 2210,
      innerHeight: 2000, outerHeight: 2805,
      power: 39.6, price: 32810
    },
    {
      id: 5,
      innerLength: 1500, outerLength: 1940,
      innerWidth: 1500, outerWidth: 2460,
      innerHeight: 2000, outerHeight: 2805,
      power: 52.8, price: 42840
    },
    {
      id: 6,
      innerLength: 2000, outerLength: 2440,
      innerWidth: 2000, outerWidth: 2960,
      innerHeight: 2000, outerHeight: 2805,
      power: 52.8, price: 64170
    },
  ], [])

  // Werden für Flächenleistung benötigt
  const irSides = 2;
  // const materialStrength = 2;
  // const tempGradientDb = 1.53
  const areaPowerDb = 30;
  const overRadiation = .2;
  const priceFactor = 24797

  const basePriceVDU = 50000;
  const meterPriceVDU = 25000;
  const meterPriceVDUHolding = 20000;

  // ######################################## INPUT ########################################

  // Motivations
  const [usedPoints, setUsedPoints] = useState(0)

  const [sliderPoints, setSliderPoints] = useState({
    "Fläche": 0,
    "Energiekosten": 0,
    "Ausbringung": 0,
    "Investvolumen": 0
  })

  const updatePoints = (motivation, points) => {
    let newPoints = { ...sliderPoints, [motivation]: points }
    let totalPoints = 0
    Object.values(newPoints).forEach((point) => {
      totalPoints = totalPoints + point;
    })
    if (totalPoints <= maxPoints) {
      setSliderPoints(newPoints)
      setUsedPoints(totalPoints)
    }
  }

  // Material
  const [selectedMaterial, setSelectedMaterial] = useState('')

  // Colors
  const [checkedColors, setCheckedColors] = useState([])
  const addToCheckedColors = (color) => setCheckedColors([...checkedColors, color])
  const removeFromCheckedColors = (color) => setCheckedColors(checkedColors.filter(item => item !== color))

  // Surface
  const [selectedSurface, setSelectedSurface] = useState('')

  // Geometry
  const [selectedGeometry, setSelectedGeometry] = useState('')

  //Dimensions
  const [length, setLength] = useState(0)
  const [width, setWidth] = useState(0)
  const [height, setHeight] = useState(0)

  // Weight
  const [weight, setWeight] = useState(0)

  // Energies
  const [zone1Energy, setZone1Energy] = useState(EnergySources[1])
  const [zone2Energy, setZone2Energy] = useState(EnergySources[1])

  // Temperatures
  const [ambientTemp, setAmbientTemp] = useState(23);
  const [processTemp, setProcessTemp] = useState(135)

  // Times
  const [heatTime, setHeatTime] = useState(25)
  const [holdTime, setHoldTime] = useState(50)

  // Production volume
  const [workdays, setWorkdays] = useState(250)
  const [shifts, setShifts] = useState(2)
  const [shiftHours, setShiftHours] = useState(8)

  const [quantity, setQuantity] = useState(20000)

  const processChartUrl = useRef('')

  // ######################################## LOGIC ########################################

  // Motivation Logic
  const remainingPoints = maxPoints - usedPoints

  const motivationIsValid = remainingPoints === 0

  // Set heatCapacity based on Material 
  const heatCapacity = useMemo(() => {
    if (selectedMaterial) {
      // console.log('Kapazität: ' + Materials[selectedMaterial].heatCapacity)
      return Materials[selectedMaterial].heatCapacity
    }
  }, [Materials, selectedMaterial])

  // Set Material Color based on checked colors
  let selectedColor = useMemo(() => {
    if (checkedColors.length > 0) {
      if (checkedColors.includes('red') || checkedColors.includes('orange') || checkedColors.includes('green') || checkedColors.includes('blue')) {
        return ('color')
      } else if (checkedColors.includes('white') || checkedColors.includes('yellow')) {
        return ('white')
      } else {
        return ('black')
      }
    }
  }, [checkedColors])

  // Calculate IR heat gradient (HE + VDU?)
  const heatGradientIR = useRef(null)
  if (selectedMaterial && selectedColor && selectedSurface) {
    heatGradientIR.current = Materials[selectedMaterial].heatGradients[selectedColor][selectedSurface]
  } else {
    heatGradientIR.current = null
  }

  // Calculate process time 
  const processTime = heatTime + holdTime

  // Calculate Delta_T 
  const deltaTemp = processTemp - ambientTemp

  // Calculate quantity per Shift
  const quantityPerShift = quantity / shifts

  // Calculate parts per Minute
  const partsPerMinute = quantityPerShift / (workdays * shifts * shiftHours * 60)

  // Calculate longest/shortest/mid
  const measures = useMemo(() => {
    let tempMeasures = { min: 0, max: 0, mid: 0 }
    let temp = [length, width, height];

    tempMeasures.min = Math.min(...temp)
    temp.splice(temp.indexOf(String(tempMeasures.min)), 1)
    tempMeasures.max = Math.max(...temp)
    temp.splice(temp.indexOf(String(tempMeasures.max)), 1)

    tempMeasures.mid = temp[0]

    return tempMeasures
  }, [length, width, height])

  // calculate belt speed in m/min
  const beltSpeed = measures.mid * distanceFactor * partsPerMinute / 1000;

  // Component volume FOR HE ONLY
  const componentVolume = useMemo(() => length / 1000 * width / 1000 * height / 1000, [length, width, height])

  // Calculate run-in/out-zone VDU ONLY
  const runInZone = measures.mid * distanceFactor

  // Calculate parts in VDU CONVECTION
  const partsInOvenVDU = processTime * partsPerMinute;
  const partsInOvenVDUCeiled = Math.ceil(processTime * partsPerMinute);

  const partsInOvenVDUHolding = Math.ceil(holdTime * partsPerMinute * .9);

  const vduOutputIncreaseFactor = partsInOvenVDUCeiled / partsInOvenVDU;

  // Calculate length VDU CONVECTION
  const lengthVDUConvection = (partsInOvenVDUCeiled * runInZone) + runInZone * 2;

  // Calculate width VDU CONVECTION
  const widthVDUConvection = measures.min + lateralConstructionWidth;

  const lengthVDUHolding = partsInOvenVDUHolding * distanceFactor * measures.mid + runInZone * 2;

  const widthVDUHolding = lateralConstructionWidth + measures.max;

  const footprintVDUHolding = (partsInOvenVDU + lengthVDUHolding) * (widthVDUHolding + partsInOvenVDU) / 1000 / 1000;

  // usable space width
  const usableSpaceWidth = measures.max * distanceFactor;

  // usable space height
  const usableSpaceHeight = measures.min * distanceFactor;

  // usable space depth
  const usableSpaceDepth = measures.mid * distanceFactor * partsInOvenVDUCeiled;

  const usableSpaceDepthHolding = measures.mid * distanceFactor * partsInOvenVDUHolding;

  // outer height vdu
  const outerHeightVDU = usableSpaceHeight + 1200 + 600;

  // calculate area of inner casing
  const areaInnerCasing = ((2 * usableSpaceWidth + 2 * usableSpaceDepth) * usableSpaceHeight + 2 * usableSpaceWidth * usableSpaceDepth) * 1.8 / 1000 / 1000;

  // calculate area of isolation
  const areaIsolation = areaInnerCasing * 1.1;

  // calcluate area of outer casing
  const areaOuterCasing = ((outerHeightVDU + widthVDUConvection) * usableSpaceDepth / 1000 / 1000);

  // calculate mass of vdu
  const vduMass = (2 * areaOuterCasing + areaInnerCasing) * steelWeight + 200;

  // calculate power demand for empty vdu
  const qVDUEmpty = 1.5 * (vduMass / 2) * .5 * (deltaTemp / 3600) / preheatTimeVDU;

  // calculate power demand with isolation
  const qVDUIsolation = areaIsolation * .1;

  // calculate powerDemand for preheating
  const qProductPreheat = (weight / 1000) * heatCapacity * (deltaTemp / 3600) / (heatTime / 60);

  // Calculate kwH OF COMPONENT with preheating for VDU
  const pComponentSecureVDU = qProductPreheat * securityFactorVDU

  // Calculate kwH OF COMPONENT with preheating for HE
  const pComponentSecureHE = qProductPreheat * securityFactorHE

  // calculate power demand for loaded VDU
  const qVDULoaded = pComponentSecureVDU * partsInOvenVDUCeiled

  // calculate kwh total for VDU
  const qSecureVDU = qVDULoaded + qVDUEmpty + qVDUIsolation;

  const amountHeatRegisterMini = Math.ceil((qVDUEmpty + qVDUIsolation) / powerHeatRegisterMini);

  const powerVDUHolding = amountHeatRegisterMini * powerHeatRegisterMini;

  // calculate amount of small heat register
  const amountHeatRegisterSmall = Math.ceil(qSecureVDU / powerHeatRegisterSmall)

  // calculate sum of small heat register power
  const powerSumHeatRegisterSmall = amountHeatRegisterSmall * powerHeatRegisterSmall;

  // calculate amount of big heat register
  const amountHeatRegisterBig = Math.ceil(qSecureVDU / powerHeatRegisterBig)

  // calculate sum of big heat register power
  const powerSumHeatRegisterBig = amountHeatRegisterBig * powerHeatRegisterBig;

  const investVDUHolding = basePriceVDU + meterPriceVDUHolding * usableSpaceDepthHolding / 1000;

  // calculate radiation time VDU
  const radiationTime = (processTemp - ambientTemp) / heatGradientIR.current;

  // calculate radiated length VDU
  const radiationLength = radiationTime * beltSpeed / 60;

  // calculate total heated length VDU
  const heatedLengthTotal = runInZone / 1000 * 2 + radiationLength

  // calculate product width VDU
  const widthTotal = (measures.max / 1000 + (2 * overRadiation)) * irSides

  // Calculate total IR area
  const areaIRVDU = heatedLengthTotal * widthTotal

  // Calculate vdu IR footprint
  const widthVDUIR = (measures.min / 1000 / 2 + .3 + .7) * 2
  const lengthVDUIR = Math.ceil(heatedLengthTotal / 1.5) * 0.4 + heatedLengthTotal

  // boosted belt speed in m/min
  const beltSpeedVDUIRBoosted = lengthVDUConvection / 1000 / processTime;
  const radiatonLengthBoosted = radiationTime * beltSpeedVDUIRBoosted / 60;
  const heatedLengthTotalBoosted = runInZone / 1000 * 2 + radiatonLengthBoosted;
  const areaIRBoostedVDU = heatedLengthTotalBoosted * widthTotal;
  const lengthIRBoostedVDU = Math.ceil(heatedLengthTotalBoosted / 1.5) * 0.4 + heatedLengthTotalBoosted;

  const outputFactorVDUIRBoosted = beltSpeedVDUIRBoosted / beltSpeed;

  // Calculate HeatEvent Data
  const calculateHeatEvent = () => {
    let results = []
    heatEvents.forEach(heatEvent => {
      let result = { id: heatEvent.id }
      result.volume = heatEvent.innerLength / 1000 * heatEvent.innerWidth / 1000 * heatEvent.innerHeight / 1000
      result.footprint = heatEvent.outerLength / 1000 * heatEvent.outerWidth / 1000
      result.longestEdge = Math.max(heatEvent.innerLength, heatEvent.innerWidth, heatEvent.innerHeight)
      result.diffToComponent = result.longestEdge - measures.max
      // result.diffToVolume = result.diffToComponent >= 0 ? result.volume / componentVolume : null
      // result.componentsInOven = result.diffToVolume * loadingFactor
      result.componentsInOven = result.volume * loadingFactor / componentVolume
      result.componentsNeeded = result.diffToComponent > 0 ? quantity / result.componentsInOven : null
      result.componentsPossible = result.diffToComponent > 0 ? workdays * shifts * shiftHours * 60 / processTime : null
      result.oneBatchPower = result.componentsInOven * pComponentSecureHE
      result.isPowerSufficient = result.oneBatchPower < heatEvent.power
      result.ovenAmount = !result.componentsNeeded || !result.componentsPossible || !result.isPowerSufficient ? null : Math.ceil(result.componentsNeeded / result.componentsPossible)
      result.totalOutput = result.ovenAmount ? result.componentsPossible * result.ovenAmount * result.componentsInOven : null
      result.totalPower = result.totalOutput ? result.ovenAmount * heatEvent.power : null
      result.totalSpace = result.ovenAmount ? result.ovenAmount * result.footprint : null
      result.totalInvestment = result.ovenAmount ? result.ovenAmount * heatEvent.price : null
      results.push(result)
    });
    let filteredResults = results.filter(heatEvent => heatEvent.totalOutput).sort((a, b) => a.totalInvestment - b.totalInvestment)
    return filteredResults
  }

  // ############################## CALCULATE RESULTS ##############################

  const idealHeatEvent = calculateHeatEvent()[0];

  // calculate footprints for systems
  const footprintHEConvection = idealHeatEvent ? idealHeatEvent.totalSpace : undefined;
  // const footprintVDUIR = lengthVDUIR * widthVDUIR;
  const footprintVDUConvection = (widthVDUConvection + spaceAroundVDU) * (lengthVDUConvection + spaceAroundVDU) / 1000 / 1000;
  const footprintVDUConvectionIR = footprintVDUHolding + lengthVDUIR * widthVDUIR;
  const footprintVDURetrofitIR = widthVDUIR * lengthIRBoostedVDU + footprintVDUConvection;

  const idealFootprint = idealHeatEvent ?
    Math.max(footprintHEConvection, footprintVDUConvection, footprintVDUConvectionIR, footprintVDURetrofitIR) :
    Math.max(footprintVDUConvection, footprintVDUConvectionIR, footprintVDURetrofitIR)

  const footprintScoreHEConvection = (1 - (footprintHEConvection / idealFootprint)) * sliderPoints.Fläche;
  // const footprintScoreVDUIR = (1 - (footprintVDUIR / idealFootprint)) * sliderPoints.Fläche;
  const footprintScoreVDUConvection = (1 - (footprintVDUConvection / idealFootprint)) * sliderPoints.Fläche;
  const footprintScoreVDUConvectionIR = (1 - (footprintVDUConvectionIR / idealFootprint)) * sliderPoints.Fläche;
  const footprintScoreVDURetrofitIR = (1 - (footprintVDURetrofitIR / idealFootprint)) * sliderPoints.Fläche;

  const footprintPercentages = idealHeatEvent ? {
    'Kammerofen konvektiv': (footprintHEConvection / idealFootprint) * 100,
    // 'Durchlaufofen Infrarot': (footprintVDUIR / idealFootprint) * 100,
    'Durchlaufofen konvektiv': (footprintVDUConvection / idealFootprint) * 100,
    'Durchlaufofen Infrarot und Konvektiv': (footprintVDUConvectionIR / idealFootprint) * 100,
    'Infrarot Booster an bestehendes System nachrüsten': (footprintVDURetrofitIR / idealFootprint) * 100,
  } : {
    // 'Kammerofen konvektiv': (footprintHEConvection / idealFootprint) * 100,
    // 'Durchlaufofen Infrarot': (footprintVDUIR / idealFootprint) * 100,
    'Durchlaufofen konvektiv': (footprintVDUConvection / idealFootprint) * 100,
    'Durchlaufofen Infrarot und Konvektiv': (footprintVDUConvectionIR / idealFootprint) * 100,
    'Infrarot Booster an bestehendes System nachrüsten': (footprintVDURetrofitIR / idealFootprint) * 100,
  }



  // calculate power for systems
  const powerHEConvection = idealHeatEvent ? idealHeatEvent.totalPower : undefined;
  const powerVDUIR = areaPowerDb * areaIRVDU;
  const powerVDUConvection = Math.min(powerSumHeatRegisterBig, powerSumHeatRegisterSmall);
  const powerVDUConvectionIR = powerVDUHolding + powerVDUIR;
  const powerVDURetrofitIR = powerVDUConvection + areaPowerDb * areaIRBoostedVDU;

  const idealPower = idealHeatEvent ?
    Math.max(powerHEConvection, powerVDUConvection, powerVDUConvectionIR, powerVDURetrofitIR) :
    Math.max(powerVDUConvection, powerVDUConvectionIR, powerVDURetrofitIR)

  const powerScoreHEConvection = (1 - (powerHEConvection / idealPower)) * sliderPoints.Energiekosten;
  // const powerScoreVDUIR = (1 - (powerVDUIR / idealPower)) * sliderPoints.Energiekosten;
  const powerScoreVDUConvection = (1 - (powerVDUConvection / idealPower)) * sliderPoints.Energiekosten;
  const powerScoreVDUConvectionIR = (1 - (powerVDUConvectionIR / idealPower)) * sliderPoints.Energiekosten;
  const powerScoreVDURetrofitIR = (1 - (powerVDURetrofitIR / idealPower)) * sliderPoints.Energiekosten;

  const powerPercentages = idealHeatEvent ? {
    'Kammerofen konvektiv': (powerHEConvection / idealPower) * 100,
    // 'Durchlaufofen Infrarot': (powerVDUIR / idealPower) * 100,
    'Durchlaufofen konvektiv': (powerVDUConvection / idealPower) * 100,
    'Durchlaufofen Infrarot und Konvektiv': (powerVDUConvectionIR / idealPower) * 100,
    'Infrarot Booster an bestehendes System nachrüsten': (powerVDURetrofitIR / idealPower) * 100,
  } : {
    // 'Kammerofen konvektiv': (powerHEConvection / idealPower) * 100,
    // 'Durchlaufofen Infrarot': (powerVDUIR / idealPower) * 100,
    'Durchlaufofen konvektiv': (powerVDUConvection / idealPower) * 100,
    'Durchlaufofen Infrarot und Konvektiv': (powerVDUConvectionIR / idealPower) * 100,
    'Infrarot Booster an bestehendes System nachrüsten': (powerVDURetrofitIR / idealPower) * 100,
  }

  // calculate output for systems
  const outputHEConvection = idealHeatEvent ? Math.round(idealHeatEvent.totalOutput) : undefined;
  // const outputVDUIR = quantity;
  const outputVDUConvection = Math.ceil(vduOutputIncreaseFactor * quantity);
  const outputVDUConvectionIR = Math.round(outputVDUConvection);
  const outputVDURetrofitIR = Math.round(outputFactorVDUIRBoosted * outputVDUConvection);

  const outputScoreHEConvection = (outputHEConvection / quantity) * sliderPoints.Ausbringung;
  // const outputScoreVDUIR = (outputVDUIR / quantity) * sliderPoints.Ausbringung;
  const outputScoreVDUConvection = (outputVDUConvection / quantity) * sliderPoints.Ausbringung;
  const outputScoreVDUConvectionIR = (outputVDUConvectionIR / quantity) * sliderPoints.Ausbringung;
  const outputScoreVDURetrofitIR = (outputVDURetrofitIR / quantity) * sliderPoints.Ausbringung;

  const outputPercentages = idealHeatEvent ? {
    'Kammerofen konvektiv': (outputHEConvection / quantity) * 100,
    // 'Durchlaufofen Infrarot': (outputVDUIR / quantity) * 100,
    'Durchlaufofen konvektiv': (outputVDUConvection / quantity) * 100,
    'Durchlaufofen Infrarot und Konvektiv': (outputVDUConvectionIR / quantity) * 100,
    'Infrarot Booster an bestehendes System nachrüsten': (outputVDURetrofitIR / quantity) * 100,
  } : {
    // 'Kammerofen konvektiv': (outputHEConvection / quantity) * 100,
    // 'Durchlaufofen Infrarot': (outputVDUIR / quantity) * 100,
    'Durchlaufofen konvektiv': (outputVDUConvection / quantity) * 100,
    'Durchlaufofen Infrarot und Konvektiv': (outputVDUConvectionIR / quantity) * 100,
    'Infrarot Booster an bestehendes System nachrüsten': (outputVDURetrofitIR / quantity) * 100,
  }

  // calculate invest for systems
  const investHEConvection = idealHeatEvent ? idealHeatEvent.totalInvestment : undefined;
  const investVDUIR = priceFactor * heatedLengthTotal * 1000 * widthTotal * 1000 / 1000000;
  const investVDUConvection = basePriceVDU + meterPriceVDU * usableSpaceDepth / 1000;
  const investVDUConvectionIR = investVDUHolding + investVDUIR;
  const investVDURetrofitIR = priceFactor * heatedLengthTotalBoosted * 1000 * widthTotal * 1000 / 1000000;

  const idealInvest = idealHeatEvent ?
    Math.max(investHEConvection, investVDUConvection, investVDUConvectionIR, investVDURetrofitIR) :
    Math.max(investVDUConvection, investVDUConvectionIR, investVDURetrofitIR)

  const investScoreHEConvection = (1 - (investHEConvection / idealInvest)) * sliderPoints.Investvolumen;
  // const investScoreVDUIR = (1 - (investVDUIR / idealInvest)) * sliderPoints.Investvolumen;
  const investScoreVDUConvection = (1 - (investVDUConvection / idealInvest)) * sliderPoints.Investvolumen;
  const investScoreVDUConvectionIR = (1 - (investVDUConvectionIR / idealInvest)) * sliderPoints.Investvolumen;
  const investScoreVDURetrofitIR = (1 - (investVDURetrofitIR / idealInvest)) * sliderPoints.Investvolumen;

  const investPercentages = idealHeatEvent ? {
    'Kammerofen konvektiv': (investHEConvection / idealInvest) * 100,
    // 'Durchlaufofen Infrarot': (investVDUIR / idealInvest) * 100,
    'Durchlaufofen konvektiv': (investVDUConvection / idealInvest) * 100,
    'Durchlaufofen Infrarot und Konvektiv': (investVDUConvectionIR / idealInvest) * 100,
    'Infrarot Booster an bestehendes System nachrüsten': (investVDURetrofitIR / idealInvest) * 100,
  } : {
    // 'Kammerofen konvektiv': (investHEConvection / idealInvest) * 100,
    // 'Durchlaufofen Infrarot': (investVDUIR / idealInvest) * 100,
    'Durchlaufofen konvektiv': (investVDUConvection / idealInvest) * 100,
    'Durchlaufofen Infrarot und Konvektiv': (investVDUConvectionIR / idealInvest) * 100,
    'Infrarot Booster an bestehendes System nachrüsten': (investVDURetrofitIR / idealInvest) * 100,
  }

  // calculate scores
  const scoreHEConvection = idealHeatEvent ? powerScoreHEConvection + footprintScoreHEConvection + outputScoreHEConvection + investScoreHEConvection : 0;
  // const scoreVDUIR = powerScoreVDUIR + footprintScoreVDUIR + outputScoreVDUIR + investScoreVDUIR;
  const scoreVDUConvection = powerScoreVDUConvection + footprintScoreVDUConvection + outputScoreVDUConvection + investScoreVDUConvection;
  const scoreVDUConvectionIR = powerScoreVDUConvectionIR + footprintScoreVDUConvectionIR + outputScoreVDUConvectionIR + investScoreVDUConvectionIR;
  const scoreVDURetrofitIR = powerScoreVDURetrofitIR + footprintScoreVDURetrofitIR + outputScoreVDURetrofitIR + investScoreVDURetrofitIR;

  const scores = idealHeatEvent ? {
    'Kammerofen konvektiv': Math.round(scoreHEConvection),
    // 'Durchlaufofen Infrarot': Math.round(scoreVDUIR),
    'Durchlaufofen konvektiv': Math.round(scoreVDUConvection),
    'Durchlaufofen Infrarot und Konvektiv': Math.round(scoreVDUConvectionIR),
    'Infrarot Booster an bestehendes System nachrüsten': Math.round(scoreVDURetrofitIR)
  } : {
    // 'Kammerofen konvektiv': Math.round(scoreHEConvection),
    // 'Durchlaufofen Infrarot': Math.round(scoreVDUIR),
    'Durchlaufofen konvektiv': Math.round(scoreVDUConvection),
    'Durchlaufofen Infrarot und Konvektiv': Math.round(scoreVDUConvectionIR),
    'Infrarot Booster an bestehendes System nachrüsten': Math.round(scoreVDURetrofitIR)
  }

  // ########## RESET ##########
  // Reset all fields
  const resetComponent = () => {
    setSelectedMaterial('')
    setCheckedColors([])
    setSelectedSurface('')
    setSelectedGeometry('')
    setLength(0)
    setWidth(0)
    setHeight(0)
  }

  const resetProcess = () => {
    setQuantity(0)
    setZone1Energy('')
    setZone2Energy('')
    setAmbientTemp(23)
    setProcessTemp(150)
    setHeatTime(25)
    setHoldTime(50)
  }

  const resetSlider = () => {
    setSliderPoints({
      "Fläche": 0,
      "Energiekosten": 0,
      "Ausbringung": 0,
      "Investvolumen": 0
    })
    setUsedPoints(0)
  }

  // Validation
  const materialIsValid = selectedMaterial !== '';
  const colorsAreValid = checkedColors.length > 0;
  const surfaceIsValid = selectedSurface !== '';
  const geometryIsValid = selectedGeometry !== '';
  const dimensionsAreValid = length !== 0 && length !== '0' && length !== '' &&
    width !== 0 && width !== '0' && width !== '' &&
    height !== 0 && height !== '0' && height !== '';
  const weightIsValid = weight !== 0 && weight !== '';

  const componentIsValid =
    materialIsValid &&
    colorsAreValid &&
    surfaceIsValid &&
    geometryIsValid &&
    dimensionsAreValid &&
    weightIsValid;

  const processIsValid =
    quantity > 0 && quantity !== '0' && quantity !== '' &&
    ambientTemp !== 0 && ambientTemp !== '0' && ambientTemp !== '' &&
    processTemp > ambientTemp &&
    heatTime !== 0 && heatTime !== '0' && heatTime !== '' &&
    holdTime !== 0 && holdTime !== '0' && holdTime !== ''

  return (
    <ComponentContext.Provider value={{
      Motivations,
      SliderTexts,
      maxPoints,
      usedPoints,
      sliderPoints,
      updatePoints,
      remainingPoints,
      motivationIsValid,
      resetSlider,

      Materials,
      selectedMaterial, setSelectedMaterial,

      Colors,
      selectedColors: checkedColors,
      addToSelectedColors: addToCheckedColors,
      removeFromSelectedColors: removeFromCheckedColors,

      Surfaces,
      selectedSurface, setSelectedSurface,

      Geometry,
      selectedGeometry, setSelectedGeometry,

      length, setLength,
      width, setWidth,
      height, setHeight,
      weight, setWeight,

      quantity, setQuantity,
      workdays, setWorkdays,
      shifts, setShifts,
      shiftHours, setShiftHours,

      EnergySources,
      zone1Energy, setZone1Energy,
      zone2Energy, setZone2Energy,

      ambientTemp, setAmbientTemp,
      processTemp, setProcessTemp,

      heatTime, setHeatTime,
      holdTime, setHoldTime,
      processTime,

      scores, footprintPercentages, powerPercentages, outputPercentages, investPercentages,
      processChartUrl,

      resetComponent, resetProcess,

      materialIsValid, colorsAreValid, surfaceIsValid, geometryIsValid, dimensionsAreValid, weightIsValid, componentIsValid,

      processIsValid,

      idealHeatEvent
    }}>
      {children}
    </ComponentContext.Provider>
  )
}

export const ComponentData = () => {
  return useContext(ComponentContext)
}

